// Synchronisierung mit Supabase. Bewertungen über SyncEngine und supabaseRemote aus platform/sync/sync.mjs (unveränderliche Ereignisse,
// Warteschlange, Abholen mit Überlappung). Dazu, was keine Bewertung ist: Ausblendungen (hidden_items), Meldungen (sentence_reports),
// Einstellungen (profiles.settings) und Einstufung (deck_enrollments.placement).
// Wann: beim Start, nach Bewertungen gebündelt (wenige Sekunden später), wenn das Netz zurückkommt, beim Verlassen und Zurückkehren
// (visibilitychange) und alle 5 Minuten, solange die App sichtbar ist. Fehler verlieren nichts: Alles bleibt lokal in der Warteschlange.
import { supabaseRemote, supabaseEventsRemote } from '../../platform/sync/sync.mjs';
import { V2_COLUMNS } from './c1-events.js';
import { isNetworkError } from './account.js';
import { PILOTS } from './local-deck.js';

export function classifyError(e) {
  if (!e) return null;
  const status = e.status || 0, code = String(e.code || ''), msg = String(e.message || e);
  if (isNetworkError(e) || status >= 500 || status === 401 || status === 408 || status === 429 || code === 'PGRST301' || /JWT|timeout|fetch/i.test(msg)) return 'offline';
  return 'error';
}

const errorText = e => {
  const code = String(e.code || ''), msg = String(e.message || e);
  if (code === '42501') return `Der Server hat Einträge abgelehnt (Zugriffsregel). Sie bleiben auf diesem Gerät. Häufige Ursache: falsch gehende Uhr am Gerät. (${msg})`;
  return `Der Server hat Einträge abgelehnt (${code || 'Fehler'}: ${msg}). Sie bleiben auf diesem Gerät.`;
};

export class SyncService {
  constructor(core, client, { debounceMs = 3000, timers = true, intervalMs = 5 * 60 * 1000, win = globalThis } = {}) {
    Object.assign(this, { core, client, debounceMs, timers, win });
    this.state = { running: false, offline: false, error: null };
    this._timer = null; this._run = null; this._again = false;
    if (timers && win.addEventListener) {
      win.addEventListener('online', () => this.syncNow());
      win.addEventListener('offline', () => { this.state.offline = true; core.emit('status'); });
      if (win.document) win.document.addEventListener('visibilitychange', () => this.syncNow());   // beim Verlassen hochladen, beim Zurückkehren abholen
      this._interval = setInterval(() => { if (!win.document || win.document.visibilityState !== 'hidden') this.syncNow(); }, intervalMs);
    }
  }

  get user() { return this.core.account && this.core.account.user; }

  // Gebündelt: nach einer Bewertung kurz warten, ob weitere folgen; bei vielen wartenden sofort
  soon() {
    if (!this.timers) return;
    if (this.core.local.engine.outbox.length >= 20) return this.syncNow();
    clearTimeout(this._timer);
    this._timer = setTimeout(() => this.syncNow(), this.debounceMs);
  }

  syncNow() {
    if (this._run) { this._again = true; return this._run; }
    this._run = this._sync().finally(() => {
      this._run = null;
      if (this._again) { this._again = false; return this.syncNow(); }
    });
    return this._run;
  }

  async _sync() {
    const core = this.core, user = this.user;
    if (!user || core.userKey !== user.id) return core.status();
    if (this.win.navigator && this.win.navigator.onLine === false) { this.state.offline = true; core.emit('status'); return core.status(); }
    const L = core.local, deck = core.deckId, uid = user.id;
    this.state.running = true; core.emit('status');
    try {
      // 1. Bewertungen: hochladen, dann abholen
      L.engine.remote = supabaseRemote(this.client, deck, { userId: uid, appVersion: core.config.appVersion });
      const { pulled } = await L.engine.sync();
      if (L !== core.local) return core.status();           // Konto während des Sendens gewechselt
      // 1b. Kartenablauf C1: eigenes Protokoll (learning_events_v2), je Deck und Pilot
      let pulled2 = 0;
      if (L.v2) {
        for(const policy of core.config.learningFlow === 'simple-v1' ? PILOTS : [L.state.pilot]) {
          const engine = L.v2For(policy);
          engine.remote = supabaseEventsRemote(this.client, { deck, policy, columns: V2_COLUMNS, userId: uid, appVersion: core.config.appVersion });
          pulled2 += (await engine.sync()).pulled;
          if (L !== core.local) return core.status();
        }
      }
      // 2. Ausblendungen
      await this._hidden(L, uid, deck);
      // 3. Meldungen
      await this._reports(L, uid, deck);
      // 4. Einstellungen und Einstufung
      const changedSettings = await this._settings(L, uid);
      const changedProfile = await this._enrollment(L, uid, deck);
      this.state.offline = false; this.state.error = null;
      if (pulled || pulled2 || changedSettings || changedProfile) { core.rederive(); core.emit('pulled'); }
    } catch (e) {
      const kind = classifyError(e);
      if (kind === 'offline') this.state.offline = true;
      else { this.state.error = errorText(e); console.warn('Sicherung abgelehnt:', e); }
    } finally {
      this.state.running = false;
      core.emit('status');
    }
    return core.status();
  }

  async _hidden(L, uid, deck) {
    const st = L.state;
    while (st.hiddenOps.length) {
      const op = st.hiddenOps[0];
      const row = { user_id: uid, deck, kind: op.kind, item_id: op.id };
      const q = op.op === 'add'
        ? this.client.from('hidden_items').upsert({ ...row, hidden_at: new Date(op.t).toISOString() }, { onConflict: 'user_id,deck,kind,item_id', ignoreDuplicates: true })
        : this.client.from('hidden_items').delete().match(row);
      const { error } = await q;
      if (error) throw error;
      st.hiddenOps.shift(); L.save();
    }
    const { data, error } = await this.client.from('hidden_items').select('kind,item_id,hidden_at').eq('deck', deck);
    if (error) throw error;
    const server = { s: {}, u: {} };
    for (const r of data) server[r.kind === 'unit' ? 'u' : 's'][r.item_id] = Date.parse(r.hidden_at) || Date.now();
    st.hidden.s = server.s; st.hidden.u = server.u;   // dasselbe Objekt behalten (die Oberfläche hält einen Verweis)
    L.save();
  }

  async _reports(L, uid, deck) {
    const st = L.state;
    if (!st.reportOutbox.length) return;
    const byId = Object.fromEntries(st.reports.map(r => [r.id, r]));
    const rows = st.reportOutbox.map(id => byId[id]).filter(Boolean).map(r => ({ id: r.id, user_id: uid, deck, sentence_id: r.sid, reasons: r.reasons, comment: r.text ? r.text.slice(0, 2000) : null, created_at: new Date(r.t).toISOString() }));
    if (rows.length) { const { error } = await this.client.from('sentence_reports').upsert(rows, { onConflict: 'id', ignoreDuplicates: true }); if (error) throw error; }
    const done = new Set(rows.map(r => r.id));
    st.reportOutbox = st.reportOutbox.filter(id => !done.has(id) && byId[id]);
    L.save();
  }

  // Einstellungen gelten für das ganze Konto. Lokal geändert: hochladen. Sonst: Stand vom Server übernehmen. Rückgabe: lokal geändert?
  async _settings(L, uid) {
    if (L.user.settingsDirty) {
      const { error } = await this.client.from('profiles').update({ settings: L.user.settings, updated_at: new Date().toISOString() }).eq('user_id', uid);
      if (error) throw error;
      L.user.settingsDirty = false; L.saveUser();
      return false;
    }
    const { data, error } = await this.client.from('profiles').select('settings').eq('user_id', uid).maybeSingle();
    if (error) throw error;
    const s = data && data.settings;
    if (!s || !Object.keys(s).length) return false;
    const next = Object.assign({}, L.user.settings, s);
    if (JSON.stringify(next) === JSON.stringify(L.user.settings)) return false;
    Object.assign(L.user.settings, next); L.saveUser();
    return true;
  }

  // Einstufung je Deck. Lokal geändert: hochladen. Sonst die vom Server übernehmen, wenn es lokal keine gibt (zweites Gerät überspringt die
  // Einstufung) oder die auf dem Server neuer ist (auf einem anderen Gerät wiederholt).
  async _enrollment(L, uid, deck) {
    const st = L.state;
    // An empty new device must not overwrite a previous placement merely because
    // assigning its default flow marked the enrollment dirty.
    if(this.core.config.learningFlow === 'simple-v1' && !st.enrolled && !st.profile.done) {
      const {data,error}=await this.client.from('deck_enrollments').select('placement').eq('deck',deck).maybeSingle();
      if(error)throw error;
      if(data?.placement?.done) {st.profile=data.placement;st.enrolled=true;st.profileDirty=false;L.save();return true;}
    }
    if (st.profileDirty || (!st.enrolled && (L.events.length || st.profile.done))) {
      // pilot nur senden, wenn gesetzt (die Spalte kommt mit Migration 20261019000000; der bisherige Ablauf braucht sie nicht)
      const row = { user_id: uid, deck, placement: st.profile, source: 'web', ...(st.pilot ? { pilot: st.pilot } : {}) };
      const { error } = await this.client.from('deck_enrollments').upsert(row, { onConflict: 'user_id,deck' });
      if (error) throw error;
      st.profileDirty = false; st.enrolled = true; L.save();
      return false;
    }
    const { data, error } = await this.client.from('deck_enrollments').select('placement').eq('deck', deck).maybeSingle();
    if (error) throw error;
    const p = data && data.placement, at = x => (x && x.placement && x.placement.at) || 0;
    if (!p || !p.done || (st.profile.done && at(p) <= at(st.profile))) return false;
    st.profile = p; st.enrolled = true; L.save();
    return true;
  }

  stop() { clearTimeout(this._timer); clearInterval(this._interval); }
}
